import { notification } from "antd";
import { useCallback } from "react";
import { SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useConstatUpdateMutation,
  useConstatUploadAttachmentsMutation,
  useCreateConstatMutation,
} from "../../../features/api/apiSlice";
import { Constat, ConstatPostValues } from "../../../types/constats";
import { formatDayJSToDateString } from "../../../utils/dates";
import { getErrorMessage } from "../../../utils/errors/errors";
import { FormConstatSchema } from "./FormConstatNew";

export default function useSubmit(isEditMode?: boolean, constat?: Constat) {
  const navigate = useNavigate();

  const [createConstat, { isLoading: isCreating }] = useCreateConstatMutation();
  const [updateConstat, { isLoading: isUpdating }] = useConstatUpdateMutation();
  const [postAttachments, { isLoading: isPostingAttachments }] =
    useConstatUploadAttachmentsMutation();

  const create: SubmitHandler<FormConstatSchema> = useCallback(
    async (values) => {
      try {
        const formattedValues: ConstatPostValues = {
          eventType: "CONSTAT",
          name: values.name,
          description: values.description,
          site: Number(values.site),
          localisation: values.localisation,
          dateEvt: formatDayJSToDateString(values.dateEvt),
          audit: Number(values.audit),
          pilotes: [Number(values.pilote)],
          gravity: Number(values.gravity),
          needsCauseAnalysis: values.needsCauseAnalysis,
          needsActions: values.needsActions,
        };

        const createdConstat = await createConstat(formattedValues).unwrap();

        notification.success({
          message: "Constat crÃ©Ã©",
          description: `Le constat ${values.name} a Ã©tÃ© crÃ©Ã© avec succÃ¨s.`,
        });

        const files: undefined | File[] = values?.attachedFiles;

        if (files?.length) {
          await postAttachments({
            constatId: createdConstat.id,
            files,
          }).unwrap();
          notification.success({
            message: "PiÃ¨ces jointes ajoutÃ©es",
          });
        }

        navigate(`/constats/${createdConstat.id}`);
      } catch (error) {
        console.error("Error creating constat", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [createConstat, navigate, postAttachments],
  );

  const update: SubmitHandler<FormConstatSchema> = useCallback(
    async (values) => {
      try {
        if (!constat?.id) {
          throw new Error("Pas d'ID de constat");
        }

        const formattedValues: ConstatPostValues = {
          eventType: "CONSTAT",
          name: values.name,
          description: values.description,
          site: Number(values.site),
          localisation: values.localisation,
          dateEvt: formatDayJSToDateString(values.dateEvt),
          audit: Number(values.audit),
          pilotes: [Number(values.pilote)],
          gravity: Number(values.gravity),
          needsCauseAnalysis: values.needsCauseAnalysis,
          needsActions: values.needsActions,
        };

        const updatedConstat = await updateConstat({
          constatId: constat.id,
          values: formattedValues,
        }).unwrap();

        notification.success({
          message: "Constat modifiÃ©",
          description: `Le constat ${values.name} a Ã©tÃ© modifiÃ© avec succÃ¨s.`,
        });

        const files: undefined | File[] = values?.attachedFiles;

        if (files?.length) {
          await postAttachments({
            constatId: updatedConstat.id,
            files,
          }).unwrap();
          notification.success({
            message: "PiÃ¨ces jointes ajoutÃ©es",
          });
        }

        navigate(`/constats/${updatedConstat.id}`);
      } catch (error) {
        console.error("Error updating constat", error);
        notification.error({
          message: "Erreur",
          description: getErrorMessage(error),
        });
      }
    },
    [updateConstat, constat?.id, navigate, postAttachments],
  );

  return {
    onSubmit: isEditMode ? update : create,
    isLoading: isCreating || isUpdating || isPostingAttachments,
  };
}
